// Production Build Trigger - MongoDB Migration
import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider } from './context/AuthContext';
import { PlayerProvider } from './context/PlayerContext';
import Navbar from './components/Navbar';
import Home from './pages/Home';

// Lazy load player components — they only render when a song is queued
const MusicPlayerBar = lazy(() => import('./components/MusicPlayerBar'));
const GlobalPlayer = lazy(() => import('./components/GlobalPlayer'));

// Lazy load route pages to optimize initial bundle size
const Explore = lazy(() => import('./pages/Explore'));
const Artists = lazy(() => import('./pages/Artists'));
const ArtistProfile = lazy(() => import('./pages/ArtistProfile'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Login = lazy(() => import('./pages/Login'));
const Signup = lazy(() => import('./pages/Signup'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const Upload = lazy(() => import('./pages/Upload'));
const AdminPanel = lazy(() => import('./pages/AdminPanel'));
const SongPlayer = lazy(() => import('./pages/SongPlayer'));
const CategoryPage = lazy(() => import('./pages/CategoryPage'));

// Premium, subtle page-loading spinner
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-[60vh]">
    <div className="w-8 h-8 border-3 border-yellow-500 border-t-transparent rounded-full animate-spin" />
  </div>
);

function App() {
  return (
    <HelmetProvider>
    <AuthProvider>
      <PlayerProvider>
        <Router>
          {/* No bg-dark-bg here — body already has the dark background and
              an opaque bg on this wrapper would cover #lcp-bg (the LCP image
              lives in index.html, outside #root, behind everything). */}
          <div className="min-h-screen">
            <Navbar />
            <div className="flex pt-16">
              <main className="flex-1 min-h-screen pb-24 md:pb-20">
                <Suspense fallback={<PageLoader />}>
                  <Routes>
                    <Route path="/" element={<Home />} />
                    <Route path="/explore" element={<Explore />} />
                    <Route path="/artists" element={<Artists />} />
                    <Route path="/artists/:id" element={<ArtistProfile />} />
                    <Route path="/dashboard" element={<Dashboard />} />
                    <Route path="/login" element={<Login />} />
                    <Route path="/signup" element={<Signup />} />
                    <Route path="/forgot-password" element={<ForgotPassword />} />
                    <Route path="/upload" element={<Upload />} />
                    <Route path="/admin" element={<AdminPanel />} />
                    <Route path="/song/:id" element={<SongPlayer />} />
                    {/* SEO category landing pages — one explicit route per
                        slug so the router doesn't swallow other top-level
                        paths. Config lives in ./config/categories.js. */}
                    <Route path="/haryanvi-folk-songs" element={<CategoryPage />} />
                    <Route path="/haryanvi-dj-songs" element={<CategoryPage />} />
                    <Route path="/haryanvi-remix-songs" element={<CategoryPage />} />
                    <Route path="/haryanvi-romantic-songs" element={<CategoryPage />} />
                    <Route path="/haryanvi-traditional-songs" element={<CategoryPage />} />
                    <Route path="/new-haryanvi-songs" element={<CategoryPage />} />
                  </Routes>
                </Suspense>
              </main>
            </div>
            <Suspense fallback={null}>
              <MusicPlayerBar />
              <GlobalPlayer />
            </Suspense>
          </div>
        </Router>
      </PlayerProvider>
    </AuthProvider>
    </HelmetProvider>
  );
}

export default App;
